"use client";

import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Box,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  FaHandshake,
  FaUserGraduate,
  FaUsers,
  FaLightbulb,
  FaBook,
  FaClipboardList,
  FaQuestionCircle,
  FaStar,
  FaStickyNote,
  FaCheckCircle,
  FaTimes,
  FaChevronDown,
  FaBullseye,
  FaHeart,
  FaTools,
  FaGraduationCap,
} from "react-icons/fa";

interface MentorGuidelinesModalProps {
  open: boolean;
  onClose: () => void;
}

export const MentorGuidelinesModal: React.FC<
  MentorGuidelinesModalProps
> = ({ open, onClose }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "10px",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: "#1976d2",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FaClipboardList />
          <Typography variant="h5" component="span" fontWeight="bold">
            Mentor Guidelines
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "white" }}>
          <FaTimes />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 3 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              bgcolor: "#f8f9fa",
              borderLeft: "4px solid #1976d2",
            }}
          >
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Purpose of the Mentorship Program
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              The mentorship program aims to provide students with guidance and support to maximize their potential and achieve their academic, personal, and professional goals. By fostering meaningful mentor-mentee relationships, the program seeks to:
            </Typography>
            <List dense>
              <ListItem sx={{ pl: 0 }}>
                <ListItemIcon sx={{ minWidth: "36px" }}>
                  <FaCheckCircle color="#1976d2" />
                </ListItemIcon>
                <ListItemText primary="Empower students to navigate academic and career challenges." />
              </ListItem>
              <ListItem sx={{ pl: 0 }}>
                <ListItemIcon sx={{ minWidth: "36px" }}>
                  <FaCheckCircle color="#1976d2" />
                </ListItemIcon>
                <ListItemText primary="To motivate students to excel in their respective fields – to the extent that they make trends in that field" />
              </ListItem>
              <ListItem sx={{ pl: 0 }}>
                <ListItemIcon sx={{ minWidth: "36px" }}>
                  <FaCheckCircle color="#1976d2" />
                </ListItemIcon>
                <ListItemText primary="Help students set and achieve realistic goals." />
              </ListItem>
              <ListItem sx={{ pl: 0 }}>
                <ListItemIcon sx={{ minWidth: "36px" }}>
                  <FaCheckCircle color="#1976d2" />
                </ListItemIcon>
                <ListItemText primary="Provide resources, knowledge, and networks to aid in their holistic development." />
              </ListItem>
              <ListItem sx={{ pl: 0 }}>
                <ListItemIcon sx={{ minWidth: "36px" }}>
                  <FaCheckCircle color="#1976d2" />
                </ListItemIcon>
                <ListItemText primary="Encourage social responsibility and community engagement among students." />
              </ListItem>
            </List>
          </Paper>

          <Typography variant="body1" sx={{ mb: 3 }}>
            As a mentor in the scholarship program, your role is pivotal in guiding and nurturing the potential of students. To ensure a meaningful mentorship experience, the following but not limited to topics below should be included in your interval conversations with the students.
          </Typography>

          <Typography
            variant="h6"
            gutterBottom
            fontWeight="bold"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "#1976d2",
              mb: 2,
            }}
          >
            <FaBook />
            Mentorship Guidelines
          </Typography>

          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<FaChevronDown />}>
              <Typography variant="h6" fontWeight="bold" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FaBook color="#1976d2" />
                1. Academic Performance
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List dense>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: "36px" }}>
                    <FaCheckCircle color="#1976d2" />
                  </ListItemIcon>
                  <ListItemText primary="Discuss the student's current academic progress and performance." />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: "36px" }}>
                    <FaCheckCircle color="#1976d2" />
                  </ListItemIcon>
                  <ListItemText primary="Identify strengths and areas for improvement." />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: "36px" }}>
                    <FaCheckCircle color="#1976d2" />
                  </ListItemIcon>
                  <ListItemText primary="Provide strategies for effective study habits and time management." />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: "36px" }}>
                    <FaCheckCircle color="#1976d2" />
                  </ListItemIcon>
                  <ListItemText primary="Encourage the pursuit of excellence and consistency in academics." />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: "36px" }}>
                    <FaCheckCircle color="#1976d2" />
                  </ListItemIcon>
                  <ListItemText primary="Reiterate the significance of education program pursued by student." />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: "36px" }}>
                    <FaCheckCircle color="#1976d2" />
                  </ListItemIcon>
                  <ListItemText primary="Keep the student motivated and re-ensure his/her confidence in his/her abilities to achieve higher results." />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<FaChevronDown />}>
              <Typography variant="h6" fontWeight="bold" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FaBullseye color="#1976d2" />
                2. Aspirations - Future Goals
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List dense>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: "36px" }}>
                    <FaCheckCircle color="#1976d2" />
                  </ListItemIcon>
                  <ListItemText primary="Understand the student's short-term and long-term goals." />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: "36px" }}>
                    <FaCheckCircle color="#1976d2" />
                  </ListItemIcon>
                  <ListItemText primary="Help the student articulate their aspirations clearly." />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: "36px" }}>
                    <FaCheckCircle color="#1976d2" />
                  </ListItemIcon>
                  <ListItemText primary="Align academic efforts with their career and personal goals." />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<FaChevronDown />}>
              <Typography variant="h6" fontWeight="bold" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FaUsers color="#1976d2" />
                3. Career, Education, Family, and Social Prospects
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List dense>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: "36px" }}>
                    <FaCheckCircle color="#1976d2" />
                  </ListItemIcon>
                  <ListItemText primary="Explore career paths that align with the student's skills and interests." />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: "36px" }}>
                    <FaCheckCircle color="#1976d2" />
                  </ListItemIcon>
                  <ListItemText primary="Discuss the importance of higher education and the potential universities or institutions." />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: "36px" }}>
                    <FaCheckCircle color="#1976d2" />
                  </ListItemIcon>
                  <ListItemText primary="Understand family expectations and how they may influence decisions and guide student on how to manage the expectations." />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: "36px" }}>
                    <FaCheckCircle color="#1976d2" />
                  </ListItemIcon>
                  <ListItemText primary="Encourage building a supportive social network and community." />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<FaChevronDown />}>
              <Typography variant="h6" fontWeight="bold" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FaHandshake color="#1976d2" />
                4. Social Contribution
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List dense>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: "36px" }}>
                    <FaCheckCircle color="#1976d2" />
                  </ListItemIcon>
                  <ListItemText primary="Inspire students to think about how they can give back to society." />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: "36px" }}>
                    <FaCheckCircle color="#1976d2" />
                  </ListItemIcon>
                  <ListItemText primary="Discuss opportunities for community service or social projects." />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: "36px" }}>
                    <FaCheckCircle color="#1976d2" />
                  </ListItemIcon>
                  <ListItemText primary="Emphasize the value of contributing to social causes and its impact on personal growth." />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<FaChevronDown />}>
              <Typography variant="h6" fontWeight="bold" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FaHeart color="#1976d2" />
                5. Personal and Financial Challenges
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List dense>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: "36px" }}>
                    <FaCheckCircle color="#1976d2" />
                  </ListItemIcon>
                  <ListItemText primary="Create a safe space for students to share their personal challenges." />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: "36px" }}>
                    <FaCheckCircle color="#1976d2" />
                  </ListItemIcon>
                  <ListItemText primary="Address any financial constraints and provide practical advice or resources." />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: "36px" }}>
                    <FaCheckCircle color="#1976d2" />
                  </ListItemIcon>
                  <ListItemText primary="Help students prioritize and manage these challenges effectively." />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<FaChevronDown />}>
              <Typography variant="h6" fontWeight="bold" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FaTools color="#1976d2" />
                6. Guidance on Skills and University Course Selection
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List dense>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: "36px" }}>
                    <FaCheckCircle color="#1976d2" />
                  </ListItemIcon>
                  <ListItemText primary="Recommend courses and skills that complement the student's goals." />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: "36px" }}>
                    <FaCheckCircle color="#1976d2" />
                  </ListItemIcon>
                  <ListItemText primary="Offer advice on selecting university programs or majors." />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: "36px" }}>
                    <FaCheckCircle color="#1976d2" />
                  </ListItemIcon>
                  <ListItemText primary="Highlight the importance of soft skills, such as communication, teamwork and entrepreneurial skills." />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: "36px" }}>
                    <FaCheckCircle color="#1976d2" />
                  </ListItemIcon>
                  <ListItemText primary="Suggest additional certifications or training programs to enhance their profiles." />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<FaChevronDown />}>
              <Typography variant="h6" fontWeight="bold" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FaUserGraduate color="#1976d2" />
                7. Career Options
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List dense>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: "36px" }}>
                    <FaCheckCircle color="#1976d2" />
                  </ListItemIcon>
                  <ListItemText primary="Provide insights into various career opportunities in their field of interest." />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: "36px" }}>
                    <FaCheckCircle color="#1976d2" />
                  </ListItemIcon>
                  <ListItemText primary="Help students understand job market trends and skill demands." />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: "36px" }}>
                    <FaCheckCircle color="#1976d2" />
                  </ListItemIcon>
                  <ListItemText primary="Share your professional experiences and lessons learned." />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<FaChevronDown />}>
              <Typography variant="h6" fontWeight="bold" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FaGraduationCap color="#1976d2" />
                8. Future Educational Aspirations
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List dense>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: "36px" }}>
                    <FaCheckCircle color="#1976d2" />
                  </ListItemIcon>
                  <ListItemText primary="Encourage the pursuit of further education aligned to their goals." />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: "36px" }}>
                    <FaCheckCircle color="#1976d2" />
                  </ListItemIcon>
                  <ListItemText primary="Discuss graduate programs, scholarships, and other learning opportunities." />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: "36px" }}>
                    <FaCheckCircle color="#1976d2" />
                  </ListItemIcon>
                  <ListItemText primary="Provide resources or contacts that could assist in their educational journey." />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>
        </Box>
      </DialogContent>
    </Dialog>
  );
}; 